import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { CircleCheck, CircleDot } from "lucide-react";
import Tooltip from "../../../../Tooltip";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../../../../utils/hooks/company";
import { firstToUpper } from "../../../../../../utils/helpers/string";
import { cn } from "../../../../../../lib/utils";

function Item({ item, index }: any) {
    const { t } = useTranslation();
    const timeFormat = useCompanyTimeFormat();
    const dateFormat = useCompanyDateFormat();

    const statusConfig = useMemo(() => {
        return item?.status === "approved" ? {
            icon: <CircleCheck className="tw-size-4 tw-text-green-600 tw-shrink-0 tw-ml-2" />,
            label: t("approved"),
        } : item?.status ? {
            icon: <CircleDot className="tw-size-4 tw-text-amber-500 tw-shrink-0 tw-ml-2" />,
            label: t("needs-approval-from-manager"),
        } : null;
    }, [item?.status, t]);

    return (
        <div className={cn("tw-flex tw-flex-col tw-gap-1.5", index > 0 && "tw-border-t tw-border-dashed tw-pt-2.5 tw-mt-2.5")}>
            {item.period?.startDate && (
                <p className="tw-text-xs tw-text-muted-foreground tw-m-0">
                    {moment(item.period.startDate).format(dateFormat)}
                    {" – "}
                    {item.period.endDate ? moment(item.period.endDate).format(dateFormat) : t("ongoing")}
                </p>
            )}
            <div className="tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-1 tw-min-w-0">
                    <span
                        className={cn(
                            "tw-text-sm tw-font-medium tw-shrink-0",
                            item.isAvailable ? "tw-text-green-700" : "tw-text-red-600"
                        )}
                    >
                        {item.isAvailable ? firstToUpper(t("available")) : t("unavailable")}
                    </span>

                    {item.allDay && (
                        <span className="tw-text-xs tw-bg-muted tw-text-muted-foreground tw-px-1.5 tw-py-0.5 tw-rounded tw-shrink-0">
                            {t("all-day")}
                        </span>
                    )}

                    {item.reason && (
                        <span className="tw-text-xs tw-text-muted-foreground tw-truncate">
                            · {item.reason}
                        </span>
                    )}
                </div>

                {statusConfig && (
                    <Tooltip trigger={statusConfig.icon}>
                        {statusConfig.label}
                    </Tooltip>
                )}
            </div>

            {!item.allDay && item?.shifts?.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1">
                    {item.shifts.map((shift: any, i: number) => (
                        <span
                            key={i}
                            className="tw-text-xs tw-font-mono tw-bg-muted tw-rounded tw-px-1.5 tw-py-0.5 tw-text-foreground/70"
                        >
                            {moment.parseZone(shift.from).format(timeFormat)}
                            {" – "}
                            {moment.parseZone(shift.to).format(timeFormat)}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Item;
